import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Divider, Toolbar, Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import EditorControls from '../../BaseEditor/editorControls';
import { AppContext } from '../../../providers/authProvider';
import DefaultCPButton from '../../../components/buttons/defaultCommandPanelButton';

const SAVE_MODES = {
  forMe: 0,
  forOrg: 1,
  forAll: 2,
};

function SaveReport({
  reportData, onHide, onSave, isNew,
}) {
  const [newReportData, setReportData] = useState({
    name: '',
    description: '',
    mode: SAVE_MODES.forMe,
    title: null,
  });
  const { currentUser } = useContext(AppContext);

  const isReadOnly = !isNew
    && !currentUser.is_superuser
    && reportData.author !== currentUser.profile;

  useEffect(() => {
    let newMode = SAVE_MODES.forAll;
    if (reportData.user !== null) {
      newMode = SAVE_MODES.forMe;
    } else if (reportData.org !== null) {
      newMode = SAVE_MODES.forOrg;
    } else if (!currentUser.is_superuser) newMode = SAVE_MODES.forMe;
    setReportData((oldParam) => ({
      ...oldParam,
      name: reportData.name,
      description: reportData.description,
      mode: newMode,
    }));
  }, [
    currentUser.is_superuser,
    reportData.description,
    reportData.name,
    reportData.org,
    reportData.user,
    reportData.author,
  ]);

  const saveHandler = useCallback(() => {
    const saveData = {
      name: newReportData.name,
      description: newReportData.description,
      schema_id: reportData.schema.id,
      settings: { title: { display: newReportData.title } },
      user:
        newReportData.mode === SAVE_MODES.forMe ? currentUser.profile : null,
      org: newReportData.mode === SAVE_MODES.forOrg ? currentUser.org.id : null,
    };
    // console.log(saveData)
    onSave(saveData, isNew);
  }, [
    newReportData.name,
    newReportData.description,
    newReportData.title,
    newReportData.mode,
    reportData.schema.id,
    currentUser.profile,
    currentUser.org.id,
    onSave,
    isNew,
  ]);

  const saveModes = useMemo(
    () => [
      {
        value: SAVE_MODES.forMe,
        display_name: 'Тількі для мене',
      },
      {
        value: SAVE_MODES.forOrg,
        display_name: 'Для моєї установи',
      },
      ...(currentUser.is_superuser
        ? [
          {
            value: SAVE_MODES.forAll,
            display_name: 'Для всіх',
            disabled: !currentUser.is_superuser,
          },
        ]
        : []),
    ],
    [currentUser.is_superuser],
  );

  return (
    <Box
      sx={{
        p: 2,
        overflow: 'auto',
        height: 1,
      }}
    >
      <EditorControls.StringInput
        controlId="name"
        label="Назва"
        value={newReportData.name}
        onChange={(e, v) => setReportData((oldV) => ({
          ...oldV,
          name: v,
        }))}
        readOnly={isReadOnly}
      />
      <EditorControls.TextInput
        controlId="description"
        label="Опис"
        value={newReportData.description}
        onChange={(e, v) => setReportData((oldV) => ({
          ...oldV,
          description: v,
        }))}
        readOnly={isReadOnly}
      />
      <Typography variant="subtitle1" color="grey.500">Додаткові налаштування</Typography>
      <Divider sx={{ mb: 2 }} />
      <Box>
        <EditorControls.Radio
          group="save_mode"
          value={newReportData.mode}
          values={saveModes}
          onChange={(e, v) => setReportData((oldV) => ({
            ...oldV,
            mode: v,
          }))}
          readOnly={isReadOnly}
          label="Режим збереження"
        />
        <EditorControls.StringInput
          label="Заголовок"
          value={newReportData.title}
          onChange={(e, v) => setReportData((oldV) => ({
            ...oldV,
            title: v,
          }))}
          readOnly={isReadOnly}
        />
      </Box>
      <Toolbar
        variant="dense"
        sx={{
          justifyContent: 'flex-end',
          mt: 1,
        }}
      >
        <DefaultCPButton
          disabled={isReadOnly}
          onClick={() => {
            saveHandler();
            onHide();
          }}
          content="Зберегти"
          color="success"
          icon={<FontAwesomeIcon icon={faCheck} />}
        />
        <DefaultCPButton
          onClick={() => {
            onHide();
          }}
          content="Закрити"
          color="secondary"
          icon={<FontAwesomeIcon icon={faXmark} />}
        />
      </Toolbar>
    </Box>
  );
}

SaveReport.propTypes = {
  onHide: PropTypes.func.isRequired,
  reportData: PropTypes.shape({
    description: PropTypes.string,
    name: PropTypes.string,
    org: PropTypes.number,
    user: PropTypes.number,
    author: PropTypes.number,
    schema: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  onSave: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
};

SaveReport.defaultProps = {
  reportData: {
    description: '',
    name: '',
    org: null,
    user: null,
    author: null,
  },
  isNew: false,
};

export default SaveReport;
